import React from 'react';
import PropTypes from 'prop-types';
import includes from 'lodash/includes';
import get from 'lodash/get';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import differenceInDays from 'date-fns/difference_in_days';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSquareCheck,
  faLaptopCode,
  faGraduationCap,
  faHeadphonesSimple,
  faBookOpen,
} from '@fortawesome/free-solid-svg-icons';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
  faAndroid as fabFaAndroid,
} from '@fortawesome/free-brands-svg-icons';

import store from 'Redux/store';
import DlcBadge from 'Components/Cards/DlcBadge';
import ProductCover from 'Components/Utility/ProductCover';
import HitCardLabel from 'Components/Cards/HitCardLabel';
import Drm from 'Components/Utility/Drm';
import { toggleCheckoutModal } from 'Redux/ducks/modal';
import FormatProductName from 'Helpers/Format/FormatProductName';
import HitLinkTo from 'Helpers/Product/HitLinkTo';
import useViewport from 'Hooks/useViewport';
import imageSizes from 'Components/Cards/image-sizes';
import PriceContainer from 'Components/Utility/Price/PriceContainer';

function BuyWithHitCard({
  hit,
  siteTheme,
  lgSize,
  mdSize,
  smSize,
  xsSize,
  notAlgolia,
  flashDeal,
  showFlashDealBar, // Allows regular hits to show bar as the flashDeal prop changes data
  hitCardLightTheme,
  hitCardDarkTheme,
  lazy,
  sizesOverride,
  place,
  labelOverride,
  clickFunction,
  inLibrary,
  inSteamLibrary,
  authenticated,
  fantasyVerse,
  coverTypeOverride,
  selectedProductIds,
  setSelectedProductIds,
  disableLink,
}) {
  const { isAboveSm } = useViewport();
  let startingDate = hit.available_valid_from;
  let endingDate = hit.available_valid_until;
  let OS = hit.operating_systems || [];
  const discountPercentOverride = hit.discount_percent_override;

  const isSrpBundle = get(hit, 'is_srp_bundle', false);
  const features = get(hit, 'features', []);
  const lowerCaseFeatures = features.map(feature => feature.toLowerCase());
  const platforms = hit.platforms;

  let freeToPlayTag = false;
  if (includes(lowerCaseFeatures, 'free to play')) {
    freeToPlayTag = true;
  }

  const onLinkClick = () => {
    if (place === 'Mega Menu') {
      clickFunction();
    }

    if (place === 'Checkout Modal') {
      store.dispatch(toggleCheckoutModal());
    }
  };

  let price = hit.price;
  let validDrm = '';
  let filteredDrm = [];
  if (hit && hit.drm && !flashDeal && !notAlgolia) filteredDrm = hit.drm.filter(d => d !== 'voucher');
  if (filteredDrm.length === 1) validDrm = filteredDrm[0];
  if (flashDeal || notAlgolia) {
    const validPlatforms = Object.keys(hit.drm).filter(d => hit.drm[d]);
    filteredDrm = validPlatforms.filter(platform => platform !== 'voucher');
    validDrm = filteredDrm[0];

    startingDate = hit.available_valid_from / 1000;
    endingDate = hit.available_valid_until / 1000;

    OS = [];
    if (platforms && platforms.windows) OS.push('windows');
    if (platforms && platforms.mac) OS.push('mac');
    if (platforms && platforms.linux) OS.push('linux');
  }

  if (notAlgolia) {
    const formattedPrice = {};
    Object.keys(price).forEach((currency) => {
      if (!formattedPrice[currency]) {
        formattedPrice[currency] = price[currency] / 100;
      }
    });
    price = formattedPrice;
  }

  let discountPercent = hit.discount_percent;

  if (hit.type === 'pick-and-mix') {
    discountPercent = hit.pnm_saving;
  }

  OS.sort().reverse();

  const name = FormatProductName(hit.name, hit.type);

  let cardTheme = 'dark';
  if (hitCardDarkTheme) cardTheme = 'dark';
  if (hitCardLightTheme || siteTheme === 'light') cardTheme = 'light';

  let productCoverSizes = imageSizes(lgSize, mdSize, smSize, xsSize);

  if (sizesOverride) productCoverSizes = sizesOverride;

  let cardType = 'standard';
  if (hit.display_type === 'comic-bundle' || hit.display_type === 'comic') cardType = 'comic';
  if (hit.display_type === 'book-bundle' || hit.display_type === 'book') cardType = 'book';
  if (hit.display_type === 'elearning-bundle' || hit.display_type === 'elearning') cardType = 'elearning';
  if (hit.display_type === 'audio-bundle' || hit.display_type === 'audio') cardType = 'audio';
  if (hit.display_type === 'software-bundle' || hit.display_type === 'software') cardType = 'software';
  if (hit.display_type === 'graphic-novel') cardType = 'graphic-novel';

  let coverType = '';
  if (hit.cover_type) coverType = hit.cover_type;
  if (coverTypeOverride) coverType = coverTypeOverride;


  let _labelOverride = labelOverride;

  if (!_labelOverride) {
    if (inLibrary && authenticated && !hit.mystery) {
      _labelOverride = 'inLibrary';
    } else if (inSteamLibrary && authenticated && !hit.mystery) {
      _labelOverride = 'inSteamLibrary';
    }
  }

  const daysLeft = differenceInDays(new Date(endingDate * 1000), new Date());
  const showTimer = !isSrpBundle && (hit.type === 'bundle' || hit.type === 'pick-and-mix') && daysLeft < 90;

  const isSelected = selectedProductIds.includes(hit.product_id);

  const LinkTag = disableLink ? React.Fragment : Link;

  const toggleSelected = () => {
    if (isSelected) {
      setSelectedProductIds(selectedProductIds.filter(id => id !== hit.product_id));
    } else {
      setSelectedProductIds([...selectedProductIds, hit.product_id]);
    }
  };

  const applyHideOverlay = false;
  return (
    <div
      className={classNames(
        'HitCard',
        { 'HitCard--dark': cardTheme === 'dark' },
        { 'HitCard--light': cardTheme === 'light' },
        { 'HitCard--square': coverType === 'square' },
        { 'HitCard--unselected': !isSelected },
        { 'HitCard--selected': isSelected },
        'faux-block-link',
      )}
    >
      <HitCardLabel
        slug={hit.slug}
        bestDiscount={hit.best_ever}
        preorderDate={hit.release_date}
        startingDate={startingDate}
        endingDate={endingDate}
        type={hit.type}
        displayType={hit.display_type}
        presale={hit.presale}
        discountPercent={hit.discount_percent}
        flashDealCard={flashDeal || showFlashDealBar}
        noReleaseDate={hit.no_release_date}
        collections={hit.collections || []}
        systemDiscount={hit.system_discount}
        labelOverride={_labelOverride}
        earlyAccessDate={hit.preorder_playable_from}
        isStarDeal={hit.star_deal}
      />

      <div className="hit-card-overlay faux-block-link">
        <div className={`overlayContent ${applyHideOverlay ? 'hide-overlayContent' : ''}`}>
          <div className="overlay-content-container">
            <div className="hit-card-game-name">
              <Link to={HitLinkTo(hit)} className="faux-block-link__overlay-link" onClick={onLinkClick}>
                {name}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div key={hit.product_id} className="HitCard__main">
        <button className="btn-reset-style BuyWith__toggle" onClick={toggleSelected}>
          {isSelected ?
            <div className="BuyWith__toggleBox BuyWith__toggleBox--selected">
              <FontAwesomeIcon icon={faSquareCheck} className="BuyWith__toggleBoxChecked" />
            </div>
            :
            <div className="BuyWith__toggleBox" key="deselected" />
          }

        </button>
        <LinkTag
          {...!disableLink &&
          {
            to: HitLinkTo(hit),
            className: 'hitCard__title faux-block-link__overlay-link',
            onClick: onLinkClick,
          }
          }
        >
          <ProductCover
            hitcard
            cover={hit.cover}
            portraitCover={hit.portrait_cover}
            alt={hit.cover_alt_text || `Product cover for ${name}`}
            sizesOverride={productCoverSizes}
            displayType={hit.display_type}
            coverType={coverType}
            lazy={lazy || place === 'Mega Menu'}
            fantasyVerse={fantasyVerse}
          />
        </LinkTag>

        <div className="hitCardStripe">
          <div className="hitCardStripe__content">
            <div className="hitCardStripe__content__purchase">
              {!showTimer &&
                <LinkTag
                  {...!disableLink && {
                    to: HitLinkTo(hit),
                    className: 'hitCardStripe__content__purchase__price faux-block-link__overlay-link',
                    onClick: onLinkClick,
                  }}
                >
                  {hit.presale && (!freeToPlayTag || hit.free_to_play) &&
                    <div className="hitCardStripe__content__purchase__price__text">
                      <FormattedMessage id="buywithcard.availablesoon" defaultMessage="Available Soon" />
                    </div>
                  }

                  {(hit.free_to_play || (hit.presale && freeToPlayTag)) &&
                    <div className="hitCardStripe__content__purchase__price__text">
                      <FormattedMessage id="buywithcard.freetoplay" defaultMessage="Free to Play" />
                    </div>
                  }

                  {!hit.presale && !hit.free_to_play && !hit.hide_price &&
                    <PriceContainer
                      discountPercent={discountPercent}
                      discountPercentOverride={discountPercentOverride}
                      drm={validDrm}
                      fullPrice={hit.fullPrice}
                      hideDiscount={hit.hide_discount}
                      lightTheme={hitCardLightTheme}
                      mystery={hit.mystery}
                      payWhatYouWant={hit.pay_what_you_want}
                      price={price}
                      showAsterisk={!!hit.addVoucher}
                      slug={hit.slug}
                      tiered={hit.tiered}
                      type={hit.type}
                    />
                  }
                </LinkTag>
              }
            </div>

            {isAboveSm &&
              <div className="hitCardStripe__content__icons">
                {cardType === 'standard' &&
                  <React.Fragment>
                    {(!isEmpty(OS) || validDrm === 'utalk') &&
                      <div className="hitCardStripe__content__icons__os">
                        {includes(OS, 'windows') &&
                          <FontAwesomeIcon icon={fabFaWindows} />
                        }
                        {includes(OS, 'mac') &&
                          <FontAwesomeIcon icon={fabFaApple} />
                        }
                        {includes(OS, 'linux') &&
                          <FontAwesomeIcon icon={fabFaLinux} />
                        }
                        {validDrm === 'utalk' &&
                          <FontAwesomeIcon icon={fabFaAndroid} />
                        }
                      </div>
                    }
                    {validDrm &&
                      <Drm drmType={validDrm} colorOverride={hitCardLightTheme ? 'dark' : ''} />
                    }
                    {hit.display_type === 'dlc' &&
                      <DlcBadge />
                    }
                  </React.Fragment>
                }
                {(cardType === 'comic' || cardType === 'book' || cardType === 'elearning' || cardType === 'audio' || cardType === 'software' || cardType === 'graphic-novel') &&
                  <div className="hitCardStripe__content__icons__nonGame">
                    {cardType === 'comic' &&
                      <>
                        <FontAwesomeIcon icon={faBookOpen} />
                        <FormattedMessage id="buywithcard.comic-bundle" defaultMessage="Comic" />
                      </>
                    }
                    {cardType === 'book' &&
                      <>
                        <FontAwesomeIcon icon={faBookOpen} />
                        <FormattedMessage id="buywithcard.book-bundle" defaultMessage="eBook" />
                      </>
                    }
                    {cardType === 'elearning' &&
                      <>
                        <FontAwesomeIcon icon={faGraduationCap} />
                        <FormattedMessage id="buywithcard.elearning-bundle" defaultMessage="eLearning" />
                      </>
                    }
                    {cardType === 'audio' &&
                      <>
                        <FontAwesomeIcon icon={faHeadphonesSimple} />
                        <FormattedMessage id="buywithcard.audio" defaultMessage="Audio" />
                      </>
                    }
                    {cardType === 'software' &&
                      <>
                        <FontAwesomeIcon icon={faLaptopCode} />
                        <FormattedMessage id="buywithcard.software" defaultMessage="Software" />
                      </>
                    }
                    {cardType === 'graphic-novel' &&
                      <>
                        <FontAwesomeIcon icon={faBookOpen} />
                        <FormattedMessage id="buywithcard.graphic-novel" defaultMessage="Graphic Novel" />
                      </>
                    }
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  );
}

BuyWithHitCard.propTypes = {
  classes: PropTypes.string,
  clickFunction: PropTypes.func,
  flashDeal: PropTypes.bool,
  notAlgolia: PropTypes.bool,
  inLibrary: PropTypes.bool,
  inSteamLibrary: PropTypes.bool,
  hit: PropTypes.shape({}),
  hitCardContainerTag: PropTypes.string,
  hitCardDarkTheme: PropTypes.bool,
  hitCardLightTheme: PropTypes.bool,
  labelOverride: PropTypes.string,
  lgSize: PropTypes.number,
  lazy: PropTypes.bool,
  mdSize: PropTypes.number,
  place: PropTypes.string,
  showFlashDealBar: PropTypes.bool,
  sizesOverride: PropTypes.string,
  smSize: PropTypes.number,
  xsSize: PropTypes.number,
  authenticated: PropTypes.bool,
  fantasyVerse: PropTypes.bool,
  coverTypeOverride: PropTypes.string,
  disableLink: PropTypes.bool,
};

BuyWithHitCard.defaultProps = {
  classes: '',
  clickFunction: () => { },
  flashDeal: false,
  notAlgolia: false,
  inLibrary: false,
  inSteamLibrary: false,
  hit: {},
  hitCardContainerTag: 'div',
  hitCardDarkTheme: false,
  hitCardLightTheme: false,
  labelOverride: '',
  lgSize: 3,
  lazy: false,
  location: {},
  mdSize: 4,
  place: '',
  showFlashDealBar: false,
  sizesOverride: null,
  smSize: 4,
  xsSize: 6,
  authenticated: false,
  fantasyVerse: false,
  coverTypeOverride: null,
  disableLink: false,
};

const mapStateToProps = ({
  auth: { _id: userId, authenticated },
  wishlist: { allSlugs: wishlistSlugs },
  cart: { allSlugs: cartSlugs },
  siteTheme,
  userLibrary,
  steamLibrary,
}, { hit: { name, slug } }) => ({
  userId,
  authenticated,
  wishlistSlugs,
  inCart: cartSlugs.includes(slug),
  siteTheme,
  inLibrary: userLibrary.includes(name),
  inSteamLibrary: steamLibrary.includes(name),
});

export default connect(mapStateToProps)(BuyWithHitCard);
